import { WordCard, Flashcard } from "../entities/wordcard.model";
import { Injectable } from '@angular/core';
import { User } from '../entities/user.model';
import { Firestore, collection, addDoc, serverTimestamp, Timestamp, doc, getDoc, getDocs, where, query } from '@angular/fire/firestore';
import { orderBy, setDoc } from "firebase/firestore";
import { collectionData } from 'rxfire/firestore';
import { concat, from, Observable } from 'rxjs';
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: 'root',
})
export class FlashcardService {

    public studyDeck$: Observable<Flashcard[]> | null = null; // Observable for the study deck

    public studySession: Flashcard[] = []; // Array to hold the current study session cards
    public currentCardIndex: number = 0; // Index of the current card in the study session

    constructor(private firestore: Firestore, private translateService: TranslateService) {}

    async createFlashCard(wordCard: WordCard, articleId: string, user: User): Promise<Flashcard> {

        let newCardRef = doc(collection(this.firestore, 'flash_cards'));

        const flashcard: Flashcard = {
            id: newCardRef.id, // Generate a new ID for the flashcard
            ...wordCard,
            userId: user.uid, // Associate the flashcard with the user
            easeFactor: 2.5,
            interval: 1,
            repetitions: 0,
            dueDate: serverTimestamp() as Timestamp,
            createdAt: serverTimestamp() as Timestamp,
            articleId: articleId,
            targetLanguage: user.targetLanguage, // The language the word is being translated to
            nativeLanguage: user.nativeLanguage // The user's native language
        };

        // Set the flash card to the newCardRef
        await setDoc(newCardRef, flashcard)
        .then(() => {
            console.log('Flashcard created successfully:', flashcard);
        })
        .catch((error) => {
            console.error('Error creating flashcard:', error);
            throw error; // Re-throw the error for further handling
        });

        // Return the flashcard with the generated ID
        return flashcard
    }

  initializeStudyDeck(userId: string, targetLanguage: string): Observable<Flashcard[]> {
    const cardsQuery = query(
      collection(this.firestore, 'flash_cards'),
      where('userId', '==', userId),
      where('targetLanguage', '==', targetLanguage),
    );
    // Initial load (one-time)
    const initial$ = from(getDocs(cardsQuery).then(snapshot =>
      snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Flashcard))
    ));
    // Real-time updates
    const realtime$ = collectionData(cardsQuery, { idField: 'id' }) as Observable<Flashcard[]>;
    // Emit initial, then switch to real-time
    this.studyDeck$ = concat(initial$, realtime$);
    return this.studyDeck$;
  }

  getFlashCardById(id: string): Observable<Flashcard | undefined> {
        const flashCardDocRef = doc(this.firestore, `flash_cards/${id}`);
        return from(getDoc(flashCardDocRef).then(docSnapshot => 
        docSnapshot.exists() ? ({ id: docSnapshot.id, ...docSnapshot.data() } as Flashcard) : undefined
        ));
  }

    // UPdate flashcard in Firestore
    updateFlashCard(flashCard: Flashcard) {
        const cardRef = doc(this.firestore, `flash_cards/${flashCard.id}`);
        return setDoc(cardRef, flashCard, { merge: true })
            .then(() => {
                console.log('Flashcard updated successfully');
            })
            .catch((error) => {
                console.error('Error updating flashcard:', error);
                throw error; // Re-throw the error for further handling
            });
    }

    async onAnswer(flashCard: Flashcard, quality: 2 | 3 | 4  | 5): Promise<void> {

        const updated = this.updateFlashcard(flashCard, quality);

        // Update the flashcard in Firestore
        await this.updateFlashCard(updated)
            .then(() => {
                console.log('Flashcard updated successfully');
                return;
            }
            )
            .catch((error) => {
                console.error('Error updating flashcard:', error);
                throw error; // Re-throw the error for further handling
            }
            );
        return; // Return the updated flashcard
    }

  updateFlashcard(card: Flashcard, quality: number): Flashcard {
    const MIN_EF = 1.3;
    let ef = card.easeFactor;
    ef = ef + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    ef = Math.max(MIN_EF, ef);

    let interval = 1;
    let repetitions = card.repetitions;
    if (quality < 3) {
      repetitions = 0;
      interval = 1;
    } else {
      repetitions += 1;
      if (repetitions === 1) interval = 1;
      else if (repetitions === 2) interval = 6;
      else interval = Math.round(card.interval * ef);
    }

    const dueDate = Timestamp.fromMillis(Date.now() + interval * 24 * 60 * 60 * 1000); // Convert days to milliseconds

    return {
      ...card,
      easeFactor: ef,
      interval,
      repetitions,
      dueDate,
    };
  }

  getTimestampMillis(ts: any): number {
    // Firestore Timestamp or JS Date
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    if (ts instanceof Date) return ts.getTime();
    if (ts.seconds) return ts.seconds * 1000;
    return 0;
  }

  createStudySession(cards: Flashcard[]): void {
    this.studySession = cards.sort((a, b) => {
      const aTime = this.getTimestampMillis(a.dueDate);
      const bTime = this.getTimestampMillis(b.dueDate);
      return aTime - bTime; // Sort by due date ascending
    });
    console.log('Study session created with cards:', this.studySession);
    this.currentCardIndex = -1; // Reset to the first card
  }

  goToNextCard(): Flashcard | null {
    if (this.currentCardIndex < this.studySession.length - 1) {
      this.currentCardIndex++;
      return this.studySession[this.currentCardIndex];
    }
    return null; // No more cards
  }

async generateDefaultCard(targetLanguage: string): Promise<Flashcard> {
  // Asynchronously create a default card with placeholder example sentences
  const sentences = await this.getExampleSentences(targetLanguage);
  let defaultCard: Flashcard = {
    root_word: 'EZ Knews',
    translation: this.translateService.instant('TOUR.WORD_CARD_TRANSLATION'),
    examples: sentences,
    example_translations: [
      this.translateService.instant('TOUR.WORD_CARD_EXAMPLE_1'),
      this.translateService.instant('TOUR.WORD_CARD_EXAMPLE_2'),
      this.translateService.instant('TOUR.WORD_CARD_EXAMPLE_3')
    ],
    articleId: '',
    id: 'default-card',
    userId: '',
    easeFactor: 2.5,
    interval: 1,
    repetitions: 0,
    dueDate: Timestamp.fromDate(new Date()),
    createdAt: Timestamp.fromDate(new Date()),
    targetLanguage: targetLanguage,
    nativeLanguage: ''
  };
  return defaultCard;
}
  getExampleSentences(targetLanguage: string): Promise<string[]> {
    let previousLang  = this.translateService.currentLang;
      const examples = ["WORD_CARD_EXAMPLE_1", "WORD_CARD_EXAMPLE_2", "WORD_CARD_EXAMPLE_3"];
      let example_sentences = this.translateService.getTranslation(targetLanguage).toPromise().then(translations =>
        examples.map(example => translations?.["TOUR"][example] || example)
      );
      //Set the language back to the user's native language
      this.translateService.use(previousLang);
      return example_sentences;
  }
}